/**
 * PreviewCardAB — "How it looks in the library" preview for an
 * Audiobook. Ports TheTappingSolutionWeb's `AudiobookCardWide`
 * (src/components/cards/AudiobookCardWide.tsx):
 *
 *   • Wide horizontal card with a dark navy/blue background
 *   • Square book cover on the left (2/5 width, rounded)
 *   • Right column: title (Asap xl semibold white), author below,
 *     yellow-orange progress bar, duration footnote
 *
 * Author isn't a first-class field on an audiobook — derived from
 * the first chapter when available so the preview matches what
 * users see (the WebApp pulls `session.author_name` from the
 * audiobook's listing payload).
 */
import React from 'react';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';
import { humanizeDuration } from '../../../utils/timeFormat';

export default function PreviewCardAB({
  state,
  totalLengthSec,
  authorName,
}) {
  const imageUrl = state.subcategory_image_preview || state.subcategory_image || '';
  const title = state.subcategory_title || 'Untitled audiobook';
  // Preview card uses the long format ("2 hr 30 min") and wants
  // `null` for missing/zero so the parent can hide the row entirely
  // rather than render a placeholder.
  const durationLabel = humanizeDuration(totalLengthSec, { units: 'long', empty: null });

  const universalLink = state.universal_link || null;
  const previewPath = universalLink
    ? universalLink.replace(/^https?:\/\//, '')
    : '— not yet generated —';

  // Synthetic progress for preview only — the audiobook in-app shows
  // a yellow→orange progress bar over the listening progress.
  // 35% is a comfortable mid-progress that demonstrates the bar
  // without claiming it's loaded data.
  const previewProgressPct = 35;

  return (
    <RailCard
      variant="preview"
      head={({ open, toggle }) => (
        <RailCardHead
          variant="preview"
          eyebrow="How it looks in the library"
          open={open}
          onToggle={toggle}
        />
      )}
    >
      {/* Stage — beige matte behind the dark audiobook card to
          mirror the prototype's tile preview stage. */}
      <div
        style={{
          padding: 16,
          background: 'linear-gradient(180deg, #FAF7F2 0%, #F5EDE4 100%)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            background: '#08293A', /* `bg-dm-bg-daily-card` analogue */
            borderRadius: 12,
            boxShadow: '0 8px 20px #2C3E5025, 0 1px 0 #FFFFFF20 inset',
            overflow: 'hidden',
          }}
        >
          {/* Base bottom-up gradient (matches the WebApp's overlay) */}
          <span
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(to top, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0) 100%)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />

          <div
            className="flex items-center"
            style={{
              padding: 14,
              gap: 14,
              minHeight: 130,
              position: 'relative',
              zIndex: 2,
            }}
          >
            {/* Left column — square book cover. The WebApp uses
                aspect-square; we cap width at 40% to mirror the
                w-2/5 / w-3/5 split. */}
            <div
              style={{
                width: '40%',
                flexShrink: 0,
                aspectRatio: '1 / 1',
                borderRadius: 8,
                overflow: 'hidden',
                background: imageUrl ? `center/cover no-repeat url("${imageUrl}")` : '#15445E',
                boxShadow: '0 4px 10px #00000040',
              }}
            />

            {/* Right column — title / author / progress / duration */}
            <div
              className="flex flex-col"
              style={{ flex: 1, minWidth: 0 }}
            >
              <h3
                style={{
                  margin: 0,
                  fontFamily: 'Asap, sans-serif',
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: '#FFFFFF',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {title}
              </h3>
              <p
                style={{
                  margin: '6px 0 0',
                  fontFamily: 'Asap, sans-serif',
                  fontSize: 11,
                  fontWeight: 400,
                  color: '#FFFFFFCC',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {authorName || 'Author —'}
              </p>

              {/* Progress bar — synthetic; cosmetic. */}
              <div
                style={{
                  marginTop: 14,
                  height: 10,
                  width: '100%',
                  background: '#153A4D',
                  borderRadius: 6,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${previewProgressPct}%`,
                    height: '100%',
                    borderRadius: 6,
                    background: 'linear-gradient(180deg, #FFC435 0%, #FF9927 100%)',
                  }}
                />
              </div>

              <p
                style={{
                  margin: '8px 0 0',
                  fontFamily: 'Asap, sans-serif',
                  fontSize: 11,
                  fontWeight: 400,
                  color: '#80B0C2',
                }}
              >
                {durationLabel || 'Length —'}
              </p>
            </div>

          </div>
        </div>
      </div>

      {/* preview-foot — URL + Open link */}
      <div
        className="flex items-center justify-between"
        style={{
          padding: '10px 14px',
          borderTop: '1px solid #E8E0CF',
          background: '#FAF4EA',
          fontSize: 11,
          gap: 8,
        }}
      >
        <span
          className="font-mono text-text-muted"
          style={{
            fontSize: 10.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            minWidth: 0,
          }}
        >
          {previewPath}
        </span>
        {universalLink && (
          <a
            href={universalLink}
            target="_blank"
            rel="noopener noreferrer"
            className="font-condensed font-bold uppercase no-underline"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.08em',
              color: '#5C7E72',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
            }}
          >
            Open ↗
          </a>
        )}
      </div>
    </RailCard>
  );
}
